/**
 * ProviderDashboard - Wrapper for unified Host Dashboard in Provider mode
 * 
 * For provider pilots (Makeup, Culinary, Hair, Nails, etc.),
 * this component loads the user's provider portal/event and renders
 * the HostDashboardV2 in provider mode.
 * 
 * Provider mode shows:
 * - ALL clients in one grid (not per-event)
 * - Client preference cards with inspo
 * - Service history, tips, reviews
 * - NO Quick Actions (provider IS the service)
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function findProviderEvent() {
      if (!user) return;
      
      try {
        const token = localStorage.getItem("session_token");
        
        // Get user's hosted events/services to find their provider portal
        const res = await fetch(`${API}/api/me/hosted-events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          const events = data.events || [];
          
          // Find a provider-type event (service provider portal)
          const providerCategories = ['makeup', 'culinary', 'hair', 'nails', 'nail', 'lash', 'wellness', 'style', 'travel', 'dispensary', 'style', 'provider'];
          const providerEventTypes = ['provider_dashboard', 'provider', 'beauty', 'service_provider'];
          
          const providerEvent = events.find(e => {
            const category = (e.pilot_category || '').toLowerCase();
            const eventType = (e.event_type || '').toLowerCase();
            return providerCategories.includes(category) || providerEventTypes.includes(eventType);
          });
          
          if (providerEvent) {
            // Redirect to the unified host dashboard with this event
            navigate(`/host/v2/${providerEvent.event_id}`, { replace: true });
            return;
          }
          
          // No provider event found - check for any event
          if (events.length > 0) {
            navigate(`/host/v2/${events[0].event_id}`, { replace: true });
            return;
          }
        }
        
        // No events found - redirect to /me with hosting section
        setError("No provider portal found. Please complete your pilot onboarding.");
        setTimeout(() => navigate('/me', { replace: true }), 2000);
        
      } catch (err) {
        console.error("Error finding provider event:", err);
        setError("Failed to load provider dashboard");
      } finally {
        setLoading(false);
      }
    }
    
    findProviderEvent();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">{error}</p>
          <p className="text-gray-500 text-sm">Redirecting to your profile...</p>
        </div>
      </div>
    );
  }

  return null;
}
